import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// ============================================================================
// Request DTOs
// ============================================================================

export interface PutAwayDiagnosticsRequest {
    itemNo: string;
    warehouseId: string;
    quantity: number;
    binGrpNo?: number;
}

// ============================================================================
// Response DTOs
// ============================================================================

export interface PutAwayDiagnosticsResponse {
    isValid: boolean;
    summary: string;
    itemValidation: ItemValidationResult;
    itemBinSizeValidation: ItemBinSizeValidationResult;
    stcBinValidation: StcBinValidationResult;
    stcBinTypeValidation: StcBinTypeValidationResult;
    stcBinGrpValidation: StcBinGrpValidationResult;
    movementPriorityValidation: StcMovementPriorityValidationResult;
    algorithmSimulation: AlgorithmSimulationResult;
    recommendation: string;
    actionItems: string[];
}

export interface ItemValidationResult {
    exists: boolean;
    itemNo?: string;
    description?: string;
    binGrpNo?: number;
    movementTypeNo?: number;
    useSharedBinYesNo?: boolean;
    itemType?: number;
    pickRuleNo?: number;
    stdBinCode?: string;
    putAwayToPickableBinYesNo?: boolean;
    issues: string[];
    warnings: string[];
}

export interface ItemBinSizeValidationResult {
    hasAnyRecords: boolean;
    recordCount: number;
    records: ItemBinSizeRecord[];
    issues: string[];
    warnings: string[];
}

export interface ItemBinSizeRecord {
    binSizeNo: number;
    binSizeDescription?: string;
    maxQuantity: number;
    putAwayPriorityNo: number;
}

export interface StcBinValidationResult {
    totalBinsInWarehouse: number;
    binsMatchingBinGrp: number;
    binsWithReservationAllowed: number;
    binsWithCorrectBinType: number;
    binsMatchingItemBinSize: number;
    availableBins: BinCandidateInfo[];
    issues: string[];
    warnings: string[];
}

export interface BinCandidateInfo {
    binCode: string;
    binGrpNo: number;
    binSizeNo: number;
    binTypeNo: number;
    reservationAllowed?: boolean;
    sharedBinYesNo?: boolean;
    currentQuantity: number;
    itemNo?: string;
}

export interface StcBinTypeValidationResult {
    hasRecords: boolean;
    recordCount: number;
    binTypes: BinTypeInfo[];
    issues: string[];
    warnings: string[];
}

export interface BinTypeInfo {
    binTypeNo: number;
    description?: string;
    defaultSharedBinYesNo?: boolean;
    defaultReservationAllowed?: boolean;
}

export interface StcBinGrpValidationResult {
    exists: boolean;
    binGrpNo?: number;
    description?: string;
    commonAvailable?: boolean;
    issues: string[];
    warnings: string[];
}

export interface StcMovementPriorityValidationResult {
    hasRecords: boolean;
    recordCount: number;
    priorities: MovementPriorityInfo[];
    issues: string[];
    warnings: string[];
}

export interface MovementPriorityInfo {
    priorityNo: number;
    fromPriority: number;
    toPriority: number;
    description?: string;
}

export interface AlgorithmSimulationResult {
    steps: AlgorithmStep[];
    finalRecommendedBin: string;
    wouldUseFallbackLocation: boolean;
    fallbackReason: string;
    binsEvaluated: BinEvaluation[];
    /** Complete proposed distribution of quantity across bins */
    proposedDistribution: ProposedBinAllocation[];
    /** Total quantity that could be allocated to bins */
    totalAllocated: number;
    /** Quantity that could not be allocated (would go to [X]) */
    remainingUnallocated: number;
    /** Original requested quantity */
    requestedQuantity: number;
    /** Summary of distribution */
    distributionSummary: string;
}

export interface ProposedBinAllocation {
    order: number;
    binCode: string;
    stcZoneID: string;
    allocatedQuantity: number;
    binMaxCapacity: number;
    currentBinQuantity: number;
    availableCapacity: number;
    allocationReason: string;
    stepNumber: number;
    stepName: string;
    isPickableBin: boolean;
    isSharedBin: boolean;
    isFallback: boolean;
}

export interface AlgorithmStep {
    stepNumber: number;
    stepName: string;
    description: string;
    result: string;
    passed: boolean;
}

export interface BinEvaluation {
    binCode: string;
    considered: boolean;
    considerationReason: string;
    accepted: boolean;
    rejectionReason: string;
    evaluationOrder: number;
}

// ============================================================================
// Context Response
// ============================================================================

export interface DiagnosticsContextResponse {
    companyNo: string;
    customerNo: string;
    operatorId: string;
    operatorName: string;
    isAuthenticated: boolean;
}

// ============================================================================
// Service
// ============================================================================

class PutAwayDiagnosticsService {
    private baseUrl = `${API_BASE_URL}/putaway-diagnostics`;

    /**
     * Run comprehensive put-away diagnostics for an item
     * Validates all master data required for put-away and simulates the algorithm step-by-step
     *
     * @param request Diagnostics request with ItemNo, WarehouseId, Quantity, and optional BinGrpNo
     * @returns Detailed diagnostic results with validation, simulation, and recommendations
     *
     * This helps diagnose why put-away operations fail and result in [X] fallback location.
     *
     * The diagnostics check:
     * - Items table: BinGrpNo, MovementTypeNo, StdBinCode, etc.
     * - ItemBinSize table: CRITICAL - must have records with MaxQuantity > 0
     * - StcBin table: Bins matching BinGrpNo and BinSizeNo
     * - StcBinType table: Default bin settings
     * - StcBinGrp table: Bin group configuration
     * - StcMovementPriority table: Priority ranges for movement
     *
     * The most common reason for [X] fallback is missing ItemBinSize records.
     */
    async diagnose(request: PutAwayDiagnosticsRequest): Promise<PutAwayDiagnosticsResponse> {
        const response = await AuthService.authenticatedPost(`${this.baseUrl}/diagnose`, request);
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || error.message || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Get current operator context for debugging diagnostics
     * Useful for verifying authentication and tenant context
     *
     * @returns Current user context information
     */
    async getContext(): Promise<DiagnosticsContextResponse> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/context`);
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || error.message || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }
}

export const putAwayDiagnosticsService = new PutAwayDiagnosticsService();
